import React, { useRef, useState } from "react";

import { useRouter } from "next/router";
import { CgArrowRight } from "react-icons/cg";

import rudderTyper from "@on-deck/shared/rudder-typer";
import { Button } from "components/button";
import { Card } from "components/card";
import { Heading } from "components/heading";
import { TextInput } from "components/text-input";
import { getCampaignData } from "utils/get-campaign-data";

interface StartApplicationCardProps {
  openContinueModal: () => void;
  startApplication: (email: string) => void;
}

export const StartApplicationCard: React.FC<React.PropsWithChildren<Readonly<StartApplicationCardProps>>> = ({
  openContinueModal,
  startApplication,
}) => {
  const [email, setEmail] = useState("");
  const formRef = useRef<HTMLFormElement>(null);
  const query = useRouter().query;

  const handleSubmit = async (): Promise<void> => {
    const isValid = formRef.current ? formRef.current.reportValidity() : false;
    if (!isValid) {
      return;
    }
    startApplication(email);

    void rudderTyper.applicationStarted(
      {
        program_cohort: typeof query?.cohortSlug === "string" ? query.cohortSlug : "unknown",
        program_slug: typeof query?.programSlug === "string" ? query.programSlug : "unknown",
      },
      { context: { campaign: getCampaignData() } },
    );
  };

  return (
    <Card className="max-w-xl m-auto mb-8 rounded-lg shadow-lg">
      <Card.Header>
        <Heading size={3} semanticSize={2} text="Start your application" className="mb-0" />
        <p className="text-sm">
          Already started?{" "}
          <a
            href="#"
            className="inline-flex items-center px-1 pt-1 text-sm text-od-blue"
            onClick={(e) => {
              e.preventDefault();
              openContinueModal();
            }}
          >
            Continue application
          </a>
        </p>
      </Card.Header>
      <Card.Body>
        <div className="mt-4">
          <form ref={formRef} data-test="editor-user-profile-form" className="h-full space-y-4">
            <TextInput
              name="email"
              type="email"
              label="Email"
              isRequired
              value={email}
              autoFocus
              // Note(Pawel): Email is not cleaned here to allow retrieving applications with original spelling
              // TODO: clean user input once the we've cleaned up the `application.email` records in the DB
              onChange={(newValue) => setEmail(newValue)}
              className="flex-1"
            />
          </form>

          <div className="mt-8">
            <Button
              size="sm"
              action={handleSubmit}
              eventName="startButtonClick"
              label={
                <span>
                  Continue <CgArrowRight className="inline w-4 h-4 text-white" />
                </span>
              }
            />
            <p className="mt-4 text-sm text-od-light-neutral">
              Your application is saved automatically so you can come back any time to complete it.
            </p>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};
